import { MAX_BUBBLE_TEXT_LENGTH } from "./comicTypes";

export const MIN_BUBBLE_FONT_SIZE = 12;

export interface BubbleTextLayout {
  lines: string[];
  fontSize: number;
  lineHeight: number;
  overflow: boolean;
}

export type MeasureText = (text: string, fontSize: number) => number;

export function sanitizeBubbleText(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .normalize("NFKC")
    .replace(/\r\n?/g, "\n")
    .replace(/[\p{Cc}\p{Cf}]/gu, (character) =>
      character === "\n" || character === "\t" ? character : "",
    )
    .replace(/\t/g, "  ")
    .replace(/[ \u00a0]+\n/g, "\n")
    .replace(/\n{4,}/g, "\n\n\n")
    .slice(0, MAX_BUBBLE_TEXT_LENGTH);
}

function breakWord(
  word: string,
  maxWidth: number,
  fontSize: number,
  measure: MeasureText,
): string[] {
  if (measure(word, fontSize) <= maxWidth) return [word];
  const chunks: string[] = [];
  let current = "";
  for (const character of word) {
    const next = current + character;
    if (current && measure(next, fontSize) > maxWidth) {
      chunks.push(current);
      current = character;
    } else {
      current = next;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

function wrapAtSize(
  text: string,
  maxWidth: number,
  fontSize: number,
  measure: MeasureText,
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    if (paragraph.length === 0) {
      lines.push("");
      continue;
    }
    let line = "";
    for (const word of paragraph.trim().split(/\s+/)) {
      for (const chunk of breakWord(word, maxWidth, fontSize, measure)) {
        const candidate = line ? `${line} ${chunk}` : chunk;
        if (line && measure(candidate, fontSize) > maxWidth) {
          lines.push(line);
          line = chunk;
        } else {
          line = candidate;
        }
      }
    }
    lines.push(line);
  }
  return lines;
}

export function layoutBubbleText(
  rawText: string,
  requestedFontSize: number,
  maxWidth: number,
  maxHeight: number,
  measure: MeasureText,
): BubbleTextLayout {
  const text = sanitizeBubbleText(rawText);
  let fontSize = Math.max(MIN_BUBBLE_FONT_SIZE, Math.floor(requestedFontSize));
  while (fontSize >= MIN_BUBBLE_FONT_SIZE) {
    const lines = wrapAtSize(text, Math.max(1, maxWidth), fontSize, measure);
    const lineHeight = fontSize * 1.18;
    if (lines.length * lineHeight <= maxHeight) {
      return { lines, fontSize, lineHeight, overflow: false };
    }
    fontSize -= 1;
  }
  const lines = wrapAtSize(text, Math.max(1, maxWidth), MIN_BUBBLE_FONT_SIZE, measure);
  const lineHeight = MIN_BUBBLE_FONT_SIZE * 1.18;
  const maximumLines = Math.max(1, Math.floor(maxHeight / lineHeight));
  return {
    lines: lines.slice(0, maximumLines),
    fontSize: MIN_BUBBLE_FONT_SIZE,
    lineHeight,
    overflow: lines.length > maximumLines,
  };
}
